import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Download,
  EyeOff,
  Loader2,
  Lock,
  RotateCcw,
  Search,
} from "lucide-react";
import clsx from "clsx";
import { api, apiUrl } from "../api";
import { EmptyState, Textarea, useToast } from "../ui";
import { useT } from "../i18n";
import { FilterPill } from "./BlogDashboard";

// ─────────────────────────────────────────────────────────────────────────────
// Evidence board (Spec docs/spec-evidence-board-v2.md §4b, P3) — executive gallery over the
// 46 claim candidates: one Variant-A card each (SVG from our own renderer, INLINED so the
// dark-mode CSS variables apply), a release toggle (use/use_framed only — drop/safety are
// internal by server rule), an optional comment and a lazy "Proof" expander.
// ─────────────────────────────────────────────────────────────────────────────

export type ViewClass = "use" | "use_framed" | "use_qualitative" | "drop" | "safety";
export type ReleaseStatus = "released" | "internal";

export interface ShownRow {
  ref: string;
  kind: string;
  endpoint?: string | null;
  ectoin_arm?: string | null;
  control_arm?: string | null;
  arm?: string | null;
  timepoint?: string | null;
  effect?: string | null;
  significance?: string | null;
  verdict?: string | null;
}

export interface OmittedRow {
  ref: string;
  reason: string;
  detail?: string | null;
  endpoint?: string | null;
  arm?: string | null;
  timepoint?: string | null;
}

export interface EvidenceReview {
  release_status: ReleaseStatus | null;
  comment: string | null;
  decided_by: string | null;
  updated_at: string | null;
}

export interface EvidenceCandidate {
  id: number;
  claim_id: string;
  study_external_id: string | null;
  effect_area: string | null;
  effect_area_en: string | null;
  view_class: ViewClass | null;
  headline_en: string | null;
  headline_key: string | null;
  key_number_calc: {
    formula: string;
    inputs: Record<string, string>;
    result: number;
    rounding?: string;
  } | null;
  shown_rows: ShownRow[] | null;
  omitted_rows?: OmittedRow[];
  omitted_count: number;
  source_not_nameable: boolean;
  source_en: string;
  design_line_en: string;
  view_note: string | null;
  caveats: string | null;
  release_status: ReleaseStatus;
  release_default: ReleaseStatus;
  release_toggleable: boolean;
  review: EvidenceReview | null;
  card_svg: string;
  card_png: string;
  chart_svg: string | null;
  chart_png: string | null;
  figure_images: { url: string; caption: string }[];
}

const CLASS_ORDER: ViewClass[] = ["use", "use_framed", "use_qualitative", "safety", "drop"];
const RELEASE_ORDER: ReleaseStatus[] = ["released", "internal"];
const CLASS_CHIP: Record<ViewClass, string> = {
  use: "bg-success text-white",
  use_framed: "bg-info text-white",
  use_qualitative: "bg-info text-white",
  safety: "bg-accent-700 text-white",
  drop: "bg-surface-3 text-text-2",
};

function toggle<T>(list: T[], v: T): T[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

// Order inside a group: use, use_framed, safety, drop; then seed order.
const rank = (c: EvidenceCandidate) => CLASS_ORDER.indexOf(c.view_class ?? "drop") * 1000 + c.id;

export default function EvidenceReviewTab({
  candidates,
  onUpdated,
}: {
  candidates: EvidenceCandidate[];
  onUpdated: (row: EvidenceCandidate) => void;
}) {
  const t = useT();
  const ev = t.blog.evidence;

  const [areas, setAreas] = useState<string[]>([]);
  const [classes, setClasses] = useState<ViewClass[]>([]);
  const [releases, setReleases] = useState<ReleaseStatus[]>([]);

  // Deep link from the released consumers (Claims tab, /claims): ?claim=<claim_id> scrolls to
  // that card ("Proof" link). Runs once the candidates are there.
  const [params] = useSearchParams();
  const focus = params.get("claim");
  useEffect(() => {
    if (!focus || candidates.length === 0) return;
    const el = document.querySelector(`[data-evi-card="${CSS.escape(focus)}"]`);
    el?.scrollIntoView({ block: "start" });
  }, [focus, candidates.length]);

  const areaValues = useMemo(() => {
    const seen = new Set<string>();
    for (const c of candidates) seen.add(c.effect_area_en || "—");
    return [...seen].sort();
  }, [candidates]);
  const count = (pred: (c: EvidenceCandidate) => boolean) => candidates.filter(pred).length;

  const shown = useMemo(
    () =>
      candidates.filter(
        (c) =>
          (!areas.length || areas.includes(c.effect_area_en || "—")) &&
          (!classes.length || classes.includes(c.view_class ?? "drop")) &&
          (!releases.length || releases.includes(c.release_status)),
      ),
    [candidates, areas, classes, releases],
  );

  const groups = RELEASE_ORDER.map((rs) => ({
    key: rs,
    items: shown.filter((c) => c.release_status === rs).sort((a, b) => rank(a) - rank(b)),
  })).filter((g) => g.items.length > 0);

  const anyFilter = areas.length || classes.length || releases.length;
  const resetAll = () => {
    setAreas([]);
    setClasses([]);
    setReleases([]);
  };

  if (candidates.length === 0) {
    return (
      <EmptyState icon={<ClipboardCheck className="h-6 w-6" />} title={ev.empty} hint={ev.emptyHint} />
    );
  }

  return (
    <div>
      <p className="mb-4 text-sm text-text-2">{ev.intro}</p>

      <div className="mb-5 space-y-2.5 rounded-card border border-border bg-surface p-3" data-evi-filters>
        <FilterRow caption={ev.filterRelease}>
          {RELEASE_ORDER.map((v) => (
            <FilterPill key={v} active={releases.includes(v)} onClick={() => setReleases((a) => toggle(a, v))}>
              <span data-evi-filter={`release:${v}`}>
                {ev.release[v]} <Count n={count((c) => c.release_status === v)} />
              </span>
            </FilterPill>
          ))}
        </FilterRow>
        <FilterRow caption={ev.filterClass}>
          {CLASS_ORDER.map((v) => (
            <FilterPill key={v} active={classes.includes(v)} onClick={() => setClasses((a) => toggle(a, v))}>
              <span data-evi-filter={`class:${v}`}>
                {ev.cls[v]} <Count n={count((c) => c.view_class === v)} />
              </span>
            </FilterPill>
          ))}
        </FilterRow>
        <FilterRow caption={ev.filterArea}>
          {areaValues.map((v) => (
            <FilterPill key={v} active={areas.includes(v)} onClick={() => setAreas((a) => toggle(a, v))}>
              <span data-evi-filter={`area:${v}`}>
                {v} <Count n={count((c) => (c.effect_area_en || "—") === v)} />
              </span>
            </FilterPill>
          ))}
          {anyFilter ? (
            <button
              onClick={resetAll}
              className="ml-auto inline-flex items-center gap-1 rounded-chip px-2.5 py-1 text-xs font-medium text-text-3 hover:bg-surface-3"
              data-evi-reset
            >
              <RotateCcw className="h-3.5 w-3.5" /> {ev.reset}
            </button>
          ) : null}
        </FilterRow>
      </div>

      <p className="mb-4 text-sm text-text-3" data-evi-count={shown.length}>
        {ev.count(shown.length, candidates.length)}
      </p>

      {shown.length === 0 ? (
        <EmptyState icon={<Search className="h-6 w-6" />} title={ev.emptyFiltered} />
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.key} data-evi-group={g.key}>
              <div className="mb-3 border-b border-border pb-2">
                <div className="flex items-baseline gap-2">
                  <h2 className="font-display text-base font-semibold text-text">{ev.groups[g.key]}</h2>
                  <span className="font-numeric text-sm tabular-nums text-text-3" data-evi-group-count>
                    {g.items.length}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-text-3">{ev.groupHint[g.key]}</p>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {g.items.map((c) => (
                  <EvidenceCard key={c.claim_id} cand={c} onUpdated={onUpdated} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterRow({ caption, children }: { caption: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-full shrink-0 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-text-3 sm:w-20">
        {caption}
      </span>
      {children}
    </div>
  );
}

function Count({ n }: { n: number }) {
  return <span className="font-numeric tabular-nums opacity-70">{n}</span>;
}

// ── Inline SVG (trusted: our own renderer, no <script>, served by our API) ───────────────
const svgCache = new Map<string, Promise<string>>();

function loadSvg(url: string): Promise<string> {
  let p = svgCache.get(url);
  if (!p) {
    p = fetch(apiUrl(url)).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.text();
    });
    p.catch(() => svgCache.delete(url));
    svgCache.set(url, p);
  }
  return p;
}

export function InlineSvg({ url, label }: { url: string; label: string }) {
  const ev = useT().blog.evidence;
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setSvg(null);
    setFailed(false);
    loadSvg(url)
      .then((s) => alive && (s.trimStart().startsWith("<svg") ? setSvg(s) : setFailed(true)))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [url]);
  if (failed) {
    return (
      <div className="rounded-card border border-dashed border-danger/50 px-3 py-6 text-center text-sm text-danger" data-evi-svg-error>
        {ev.svgFailed}
      </div>
    );
  }
  if (!svg) {
    return <div className="aspect-[440/360] animate-pulse rounded-[12px] bg-surface-2" aria-busy="true" />;
  }
  return (
    <div
      className="evi-svg"
      role="img"
      aria-label={label}
      data-evi-svg-ok
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// ── One card ───────────────────────────────────────────────────────────────────────────
function EvidenceCard({
  cand,
  onUpdated,
}: {
  cand: EvidenceCandidate;
  onUpdated: (row: EvidenceCandidate) => void;
}) {
  const t = useT();
  const ev = t.blog.evidence;
  const toast = useToast();
  const cls = cand.view_class ?? "drop";
  const internal = cand.release_status === "internal";
  const withdrawn = internal && cand.release_toggleable;

  const savedComment = cand.review?.comment ?? "";
  const [comment, setComment] = useState(savedComment);
  const [busy, setBusy] = useState<"release" | "comment" | null>(null);
  const [proofOpen, setProofOpen] = useState(false);
  useEffect(() => setComment(cand.review?.comment ?? ""), [cand.review?.comment]);

  const put = async (body: Record<string, unknown>, kind: "release" | "comment", okMsg: string) => {
    setBusy(kind);
    try {
      const row = await api.put<EvidenceCandidate>(
        `/api/evidence-review/${encodeURIComponent(cand.claim_id)}/release`,
        body,
      );
      onUpdated(row); // the PUT response is the new truth (no refetch: commit lands after the response)
      toast.ok(okMsg);
    } catch (e) {
      toast.err(ev.saveFailed, String(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleRelease = () => {
    const next: ReleaseStatus = internal ? "released" : "internal";
    put({ release_status: next }, "release", next === "released" ? ev.releasedToast : ev.withdrawnToast);
  };

  const dirty = comment.trim() !== savedComment.trim();

  return (
    <article
      className={clsx(
        "flex min-w-0 flex-col gap-3 rounded-card border bg-surface p-3",
        internal ? "border-danger/30" : "border-border",
      )}
      data-evi-card={cand.claim_id}
      data-evi-class={cls}
      data-evi-release={cand.release_status}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={clsx("rounded-chip px-2 py-0.5 text-[0.72rem] font-bold", CLASS_CHIP[cls])}>
          {ev.cls[cls]}
        </span>
        <span className="text-xs text-text-3">{cand.effect_area_en}</span>
        {cand.source_not_nameable && (
          <span className="rounded-chip border border-warning/60 px-2 py-0.5 text-[0.7rem] font-medium text-warning">
            {ev.sourceNotNameable}
          </span>
        )}
      </div>

      {internal && (
        <div
          className="flex items-center gap-1.5 rounded-control bg-danger-soft px-2.5 py-1.5 text-[0.8rem] font-semibold text-danger"
          data-evi-internal-marker
        >
          {withdrawn ? <EyeOff className="h-4 w-4 shrink-0" /> : <Lock className="h-4 w-4 shrink-0" />}
          <span>
            {ev.internalMarker}
            {withdrawn && <span className="font-normal"> · {ev.withdrawn}</span>}
          </span>
        </div>
      )}

      <InlineSvg url={cand.card_svg} label={cand.headline_en ?? cand.claim_id} />

      {/* Release + review meta */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {cand.release_toggleable ? (
          <button
            type="button"
            role="switch"
            aria-checked={!internal}
            onClick={toggleRelease}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-control px-1 py-1 text-sm font-medium text-text hover:bg-surface-3 disabled:opacity-60"
            data-evi-toggle
          >
            <span
              className={clsx(
                "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
                internal ? "bg-surface-3" : "bg-success",
              )}
            >
              <span
                className={clsx(
                  "absolute h-4 w-4 rounded-full bg-white shadow transition-transform",
                  internal ? "translate-x-0.5" : "translate-x-[1.125rem]",
                )}
              />
            </span>
            {busy === "release" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {internal ? ev.toggleOff : ev.toggleOn}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-text-3" data-evi-locked>
            <Lock className="h-3.5 w-3.5" /> {ev.locked}
          </span>
        )}
        {cand.review?.decided_by && (
          <span className="text-xs text-text-3" data-evi-review-by>
            {ev.lastBy(cand.review.decided_by, cand.review.updated_at)}
          </span>
        )}
      </div>

      {/* Comment */}
      <div className="space-y-1.5">
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={ev.commentPlaceholder}
          aria-label={ev.comment}
          rows={2}
          className="text-sm"
          data-evi-comment
        />
        {dirty && (
          <button
            onClick={() => put({ comment: comment.trim() || null }, "comment", ev.commentSaved)}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 rounded-control bg-accent-500 px-3 py-1 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-50"
            data-evi-comment-save
          >
            {busy === "comment" && <Loader2 className="h-4 w-4 animate-spin" />}
            {ev.saveComment}
          </button>
        )}
      </div>

      <button
        onClick={() => setProofOpen((o) => !o)}
        aria-expanded={proofOpen}
        className="inline-flex items-center gap-1 self-start rounded-control px-1 py-0.5 text-sm font-semibold text-text hover:bg-surface-3"
        data-evi-proof-toggle
      >
        {proofOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {ev.proof}
        <span className="font-normal text-text-3">
          · {ev.shownOmitted(cand.shown_rows?.length ?? 0, cand.omitted_count)}
        </span>
      </button>
      {proofOpen && <Proof cand={cand} />}
    </article>
  );
}

// ── Proof (lazy: omitted rows come from GET /api/evidence-review/{claim_id}) ─────────────
function Proof({ cand }: { cand: EvidenceCandidate }) {
  const ev = useT().blog.evidence;
  const [omitted, setOmitted] = useState<OmittedRow[] | null>(cand.omitted_rows ?? null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (omitted) return;
    let alive = true;
    api
      .get<EvidenceCandidate>(`/api/evidence-review/${encodeURIComponent(cand.claim_id)}`)
      .then((d) => alive && setOmitted(d.omitted_rows ?? []))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [cand.claim_id, omitted]);

  const calc = cand.key_number_calc;
  const downloads = [
    { label: ev.cardSvg, url: cand.card_svg },
    { label: ev.cardPng, url: cand.card_png },
    { label: ev.chartSvg, url: cand.chart_svg },
    { label: ev.chartPng, url: cand.chart_png },
  ].filter((d): d is { label: string; url: string } => !!d.url);

  return (
    <div className="space-y-3 rounded-card border border-border bg-surface-2 p-3 text-[0.82rem] text-text-2" data-evi-proof>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="font-semibold text-text-3">{ev.source}</dt>
        <dd className="min-w-0 break-words text-text" data-evi-proof-source>
          {cand.source_en}
          {cand.source_not_nameable && <span className="text-warning"> · {ev.sourceNotNameable}</span>}
        </dd>
        <dt className="font-semibold text-text-3">{ev.design}</dt>
        <dd className="min-w-0 break-words text-text">{cand.design_line_en}</dd>
      </dl>

      <ProofSection title={ev.shownRows(cand.shown_rows?.length ?? 0)}>
        <ul className="space-y-1">
          {(cand.shown_rows ?? []).map((r) => (
            <li key={r.ref} className="break-words">
              <span className="font-mono text-[0.72rem] text-success">{r.ref}</span>{" "}
              <span className="text-text">{r.endpoint}</span>
              {(r.ectoin_arm || r.arm) && (
                <span className="text-text-3">
                  {" "}
                  · {r.ectoin_arm ?? r.arm}
                  {r.control_arm ? ` vs ${r.control_arm}` : ""}
                </span>
              )}
              {r.timepoint && <span className="text-text-3"> · {r.timepoint}</span>}
              {(r.significance || r.verdict) && (
                <span className="font-semibold text-text"> — {r.significance ?? r.verdict}</span>
              )}
            </li>
          ))}
        </ul>
      </ProofSection>

      <ProofSection title={ev.omittedRows(cand.omitted_count)}>
        {failed ? (
          <p className="text-danger">{ev.loadFailed}</p>
        ) : !omitted ? (
          <p className="inline-flex items-center gap-1.5 text-text-3">
            <Loader2 className="h-4 w-4 animate-spin" /> {ev.loading}
          </p>
        ) : omitted.length === 0 ? (
          <p className="text-text-3">—</p>
        ) : (
          <ul className="max-h-64 space-y-1 overflow-y-auto pr-1" data-evi-omitted>
            {omitted.map((r, i) => (
              <li key={`${r.ref}-${i}`} className="break-words">
                <span className="font-mono text-[0.72rem] text-text-3">{r.ref}</span>{" "}
                <span className="rounded-chip bg-surface-3 px-1.5 text-[0.7rem] font-semibold text-text-2">
                  {r.reason}
                </span>{" "}
                <span className="text-text">{r.endpoint}</span>
                {r.arm && <span className="text-text-3"> · {r.arm}</span>}
                {r.timepoint && <span className="text-text-3"> · {r.timepoint}</span>}
                {r.detail && <span className="text-text-3"> — {r.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </ProofSection>

      {calc && (
        <ProofSection title={ev.keyNumber}>
          <p className="font-mono text-[0.75rem] text-text" data-evi-formula>
            {calc.formula} = {calc.result}
          </p>
          <p className="text-text-3">
            {Object.entries(calc.inputs)
              .map(([k, v]) => `${k} = ${v}`)
              .join(" · ")}
            {calc.rounding === "floor" && ` · ${ev.roundedDown}`}
          </p>
        </ProofSection>
      )}

      {(cand.view_note || cand.caveats) && (
        <ProofSection title={ev.caveats}>
          {cand.view_note && <p className="text-text">{cand.view_note}</p>}
          {cand.caveats && (
            <p className="mt-1 text-text-3">
              <span className="font-semibold">{ev.caveatsOriginal}: </span>
              {cand.caveats}
            </p>
          )}
        </ProofSection>
      )}

      {cand.figure_images.length > 0 && (
        <ProofSection title={ev.figures}>
          <div className="space-y-2">
            {cand.figure_images.map((f) => (
              <figure key={f.url} className="m-0">
                <img
                  src={apiUrl(f.url)}
                  alt={f.caption || ev.figures}
                  loading="lazy"
                  className="w-full rounded-control border border-border bg-white"
                  data-evi-figure
                />
                {f.caption && <figcaption className="mt-1 text-xs text-text-3">{f.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </ProofSection>
      )}

      <ProofSection title={ev.downloads}>
        <div className="flex flex-wrap gap-1.5">
          {downloads.map((d) => (
            <a
              key={d.label}
              href={apiUrl(d.url)}
              download
              className="inline-flex items-center gap-1 rounded-chip bg-surface px-2.5 py-1 text-xs font-medium text-text-2 hover:bg-surface-3"
              data-evi-download
            >
              <Download className="h-3.5 w-3.5" /> {d.label}
            </a>
          ))}
        </div>
      </ProofSection>
    </div>
  );
}

function ProofSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-1 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-text-3">{title}</h4>
      {children}
    </section>
  );
}
